import React from 'react';
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  Flex,
  Icon,
  Grid
} from '@chakra-ui/react';
import { 
  FaWallet, 
  FaHandHoldingUsd, 
  FaChartLine, 
  FaTicketAlt
} from 'react-icons/fa';
import { useBalance } from '../context/BalanceContext';

const UserStatsBoxes = ({ userStats }) => {
  // Get the live balance from BalanceContext
  const { balance } = useBalance();
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
      {/* Balance Box */}
      <Box 
        p={3} 
        bg="rgba(23, 25, 35, 0.9)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="blue.500"
        boxShadow="0 0 10px rgba(66, 153, 225, 0.3)"
        transition="all 0.2s"
        _hover={{ transform: "translateY(-3px)", boxShadow: "0 0 15px rgba(66, 153, 225, 0.5)" }}
        h="150px"
        position="relative"
        overflow="hidden"
      >
        <Heading size="xs" mb={3} color="blue.400" display="flex" alignItems="center">
          <Icon as={FaWallet} mr={2} boxSize={3} />
          Balance
        </Heading>
        <Flex direction="column" h="calc(100% - 30px)" justify="space-between">
          <Grid templateColumns="1fr 1fr" gap={4}>
            <Box>
              <Text fontSize="xs" color="gray.500">SOL</Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.300">{balance?.sol || "0"}</Text>
              <Text fontSize="xs" color="gray.500">(${((balance?.sol || 0) * 75.32).toFixed(2)})</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">JINU</Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.300">{(balance?.jinu || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.500">(${((balance?.jinu || 0) * 0.028).toFixed(2)})</Text>
            </Box>
          </Grid>
          <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1} borderTopWidth="1px" borderColor="blue.700" pt={1}>
            <Text>Total Value:</Text>
            <Text color="blue.300" fontWeight="bold">
              ${(
                ((balance?.sol || 0) * 75.32) + 
                ((balance?.jinu || 0) * 0.028)
              ).toFixed(2)}
            </Text>
          </Flex>
        </Flex>
      </Box>
      
      {/* Borrow/Lend Box */}
      <Box 
        p={3} 
        bg="rgba(23, 25, 35, 0.9)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="purple.500"
        boxShadow="0 0 10px rgba(159, 122, 234, 0.3)"
        transition="all 0.2s"
        _hover={{ transform: "translateY(-3px)", boxShadow: "0 0 15px rgba(159, 122, 234, 0.5)" }}
        h="150px"
        position="relative"
        overflow="hidden"
      >
        <Heading size="xs" mb={3} color="purple.400" display="flex" alignItems="center">
          <Icon as={FaHandHoldingUsd} mr={2} boxSize={3} />
          Borrow / Lend
        </Heading>
        <Flex direction="column" h="calc(100% - 30px)" justify="space-between">
          <Flex justify="space-between">
            <Box>
              <Text fontSize="xs" color="gray.500">Active Loans</Text>
              <Text fontSize="lg" fontWeight="bold" color="purple.300">{userStats?.loans?.active || "0"}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Interest Earned</Text>
              <Text fontSize="lg" fontWeight="bold" color="purple.300">{userStats?.loans?.interestEarned || "0"}</Text>
            </Box>
          </Flex>
          <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1} borderTopWidth="1px" borderColor="purple.700" pt={1}>
            <Text>Next Due:</Text>
            <Text color="purple.300" fontWeight="bold">{userStats?.loans?.nextDueDate || "N/A"}</Text>
          </Flex>
        </Flex>
      </Box>
      
      {/* Profit/Loss Box */}
      <Box 
        p={3} 
        bg="rgba(23, 25, 35, 0.9)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="green.500"
        boxShadow="0 0 10px rgba(72, 187, 120, 0.3)"
        transition="all 0.2s"
        _hover={{ transform: "translateY(-3px)", boxShadow: "0 0 15px rgba(72, 187, 120, 0.5)" }}
        h="150px"
        position="relative"
        overflow="hidden"
      >
        <Heading size="xs" mb={3} color="green.400" display="flex" alignItems="center">
          <Icon as={FaChartLine} mr={2} boxSize={3} />
          Profit / Loss
        </Heading>
        <Flex direction="column" h="calc(100% - 30px)" justify="space-between">
          <Flex justify="space-between">
            <Box>
              <Text fontSize="xs" color="gray.500">24h</Text>
              <Text fontSize="lg" fontWeight="bold" color="green.300">
                {userStats?.profitLoss?.["24h"] > 0 ? "+" : ""}{userStats?.profitLoss?.["24h"] || "0"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">7d</Text>
              <Text fontSize="lg" fontWeight="bold" color="green.300">
                {userStats?.profitLoss?.["7d"] > 0 ? "+" : ""}{userStats?.profitLoss?.["7d"] || "0"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">31d</Text>
              <Text fontSize="lg" fontWeight="bold" color="green.300">
                {userStats?.profitLoss?.["31d"] > 0 ? "+" : ""}{userStats?.profitLoss?.["31d"] || "0"}
              </Text>
            </Box>
          </Flex>
          <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1} borderTopWidth="1px" borderColor="green.700" pt={1}>
            <Text>All-time:</Text>
            <Text color="green.300" fontWeight="bold">+92,500</Text>
          </Flex>
        </Flex>
      </Box>
      
      {/* Tickets & Bonuses Box */}
      <Box 
        p={3} 
        bg="rgba(23, 25, 35, 0.9)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="pink.500"
        boxShadow="0 0 10px rgba(237, 100, 166, 0.3)"
        transition="all 0.2s"
        _hover={{ transform: "translateY(-3px)", boxShadow: "0 0 15px rgba(237, 100, 166, 0.5)" }}
        h="150px"
        position="relative"
        overflow="hidden"
      >
        <Heading size="xs" mb={3} color="pink.400" display="flex" alignItems="center">
          <Icon as={FaTicketAlt} mr={2} boxSize={3} />
          Tickets & Bonuses
        </Heading>
        <Flex direction="column" h="calc(100% - 30px)" justify="space-between">
          <Flex justify="space-between">
            <Box>
              <Text fontSize="xs" color="gray.500">Lottery Entries</Text>
              <Text fontSize="lg" fontWeight="bold" color="pink.300">{userStats?.tickets || "0"}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Gift Box Entries</Text>
              <Text fontSize="lg" fontWeight="bold" color="pink.300">{userStats?.bonusGames || "0"}</Text>
            </Box>
          </Flex>
          <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1} borderTopWidth="1px" borderColor="pink.700" pt={1}>
            <Text>Next Draw:</Text>
            <Text color="pink.300" fontWeight="bold">12h 34m</Text>
          </Flex>
        </Flex>
      </Box>
    </SimpleGrid>
  );
};

export default UserStatsBoxes;
